import Header from "~/ui/header";
import createHeaderButton from "~/ui/headerbutton";
import { SHARE_FILLED_16 } from "@sznds/icons";
import { createDashboardList, createDashboardSections, fetchDashboard } from "~/util/dashboard";
import { getItem, setItem } from "~/es6/localstorage";
import { store } from "~/state/store";
import { watch, watchCurrentUser } from "~/state/util";
import { _ } from "~/i18n";
import MyMapsShare from "~/es6/mymaps.share";
import { ELoginState, IUserInfo } from "~/state/user/interfaces";
import { node } from "~/util/dom";
import { Unsubscribe } from "redux";
import FandaStatus from "~/ui/mymaps/fandaStatus";
import { getUserFandaProgress } from "~/util/providers/premium";
import { CONFIG } from "~/config";

export default class Dashboard {
	_header: Header;

	_sections: HTMLDivElement;

	_container: HTMLDivElement;

	_list: HTMLDivElement;

	_loading: boolean;

	_storeUnsubscribe: Unsubscribe;

	_isActive: boolean;

	_data: any;

	#fanda: FandaStatus;

	constructor() {
		const { user } = store.getState().user;

		this._data = null;
		this._loading = false;
		this._container = node("div", { className: "dashboard-main-cont" });
		this._header = new Header({
			title: _("mymap.stateswitch"),
			avatar: user.avatar,
			premium: user.premium ? "mapy" : "none",
		});
		this._container.appendChild(this._header.node);

		// seznam sekci
		this._sections = node("div", { className: "dashboard-section-list" });
		this._createDashboardSections(user);
		this._container.appendChild(this._sections);

		// fanda
		this.#fanda = new FandaStatus();

		this._container.appendChild(this.#fanda.container);

		// seznam poslednich akci
		this._list = node("div", { className: "dashboard-last-list" });
		this._container.appendChild(this._list);

		const wt = watch(store.getState, "user", watchCurrentUser);

		this._storeUnsubscribe = store.subscribe(wt(newUser => {
			this._data = null;

			this._createDashboardSections(newUser.user);

			this._header.avatar = newUser.user.avatar;
			this._header.premium = newUser.user.premium ? "mapy" : "none";

			if (newUser.user.userPublicId && newUser.user.state === ELoginState.Green) {
				Mapy.getComponent("mymaps").isActive() && this._loadDashboard();
			} else {
				this._list.innerHTML = "";
				this._list.appendChild(createDashboardList(
					{},
					() => {
						this._loadDashboard();
					},
					true,
				));
			}

			// pokud tady na to zrovna koukame a zmeni se uzivatel, tak prenacteme data, jinak to delame jen pri activate
			if (Mapy.getComponent("mymaps").isActive()) {
				getUserFandaProgress().then(data => {
					this.#fanda.setData(data, newUser.user);
				});
			}
		}));
	}

	destroy() {
		this._clearList();
		this._container.remove();
		this._storeUnsubscribe();
	}

	activate() {
		if (this._isActive) {
			return this;
		}

		this._isActive = true;
		this._setState();

		if (!getItem("onboardingDashboard")) {
			setItem("onboardingDashboard", "1");
		}

		Mapy.getComponent("logger").log("view", { text: "dashboard" });

		this._clearList();

		const { user } = store.getState().user;

		this._createDashboardSections(user);

		if (user.state === ELoginState.Green) {
			this._loadDashboard();
		}

		// dotazeni dat pro fandu, kdykoliv se dashboard aktivuje (aby mel uzivatel sanci si to prenacist bez reloadu cele stranky)
		getUserFandaProgress().then(data => {
			this.#fanda.setData(data, user);
		});

		return this;
	}

	deactivate() {
		if (!this._isActive) {
			return this;
		}

		this._clearList();
		this._isActive = false;

		return this;
	}

	get isActive() {
		return this._isActive;
	}

	get container() {
		return this._container;
	}

	// eslint-disable-next-line class-methods-use-this
	set state(_state) {
	}

	// eslint-disable-next-line class-methods-use-this
	get state() {
		return {};
	}

	// eslint-disable-next-line class-methods-use-this
	_setState() {
		const { user } = store.getState().user;

		if (user.state !== ELoginState.Green) {
			Mapy.getComponent("mymaps").activeComponent();
		}
	}

	async _loadDashboard() {
		if (this._loading) {
			return;
		}

		this.setSEO();

		this._loading = true;

		try {
			const response = await fetchDashboard("me");

			this._loading = false;

			if (response.status === 200) {
				const json = await response.json();
				const { user } = store.getState().user;

				this._data = json.data;

				this._sections.innerHTML = "";
				this._sections.appendChild(createDashboardSections({
					data: this._data,
					isCurrentUser: true,
					userPublicId: user.userPublicId,
				}));

				// analytika, pokud ma navrhy na hodnoceni
				if (this._data?.reviews_proposal?.newProposalCount) {
					Mapy.getComponent("logger").log("notification_impress", { type: "suggestionReviews", origin: "dashboard" });
				}

				// je treba promazat predchozi tlacitka, protoze hlavicka zustava a tedy by se tlacitka mnozila
				this._header.rightButtons.innerHTML = "";
				// sdileci tlacitko
				const shareButton = createHeaderButton({
					icon: SHARE_FILLED_16,
					innerHTML: _("mymap.contextMenu.share"),
				});

				this._header.rightButtons.appendChild(shareButton);
				shareButton.addEventListener("click", () => {
					let sharePopup = new MyMapsShare({
						type: "dashboard",
						url: `${location.protocol}//${CONFIG.BASE_URL}/?up=${user.userPublicId}`,
						noFrame: true,
						text: node("p", {}, _("mymap.dashboard.shareText")),
					}, () => {
						sharePopup.destroy();
						sharePopup = null;
					});

					sharePopup.open();
				});

				this._list.innerHTML = "";
				this._list.appendChild(createDashboardList(
					this._data,
					() => {
						this._loadDashboard();
					},
					true,
				));
			} else {
				throw new Error(`Dashboard mel stav ${response.status}.`);
			}
		} catch (ex) {
			this._clearList();
			this._loading = false;
		}
	}

	// eslint-disable-next-line class-methods-use-this
	setSEO() {
		Mapy.SEO.set(_("mymap.stateswitch"));
	}

	_clearList() {
		this._list.innerHTML = "";
		this._list.appendChild(createDashboardList(
			this._data,
			() => {
				this._loadDashboard();
			},
			true,
		));
	}

	_createDashboardSections(user: IUserInfo) {
		this._sections.innerHTML = "";
		this._sections.appendChild(createDashboardSections({
			data: this._data || {},
			isCurrentUser: true,
			userPublicId: user.userPublicId,
		}));
	}
}
